import React, { ReactNode, useState, useEffect } from 'react';

import { TreeItem2 as MUITreeItem } from '@mui/x-tree-view/TreeItem2';
import classNames from 'classnames';

import { ColoredChip } from '../ColoredChip';
import { Icon } from '../Icon';
import { EllipsisText } from '../EllipsisText';
import { Box } from '../Box';
import { CircularProgress } from '../CircularProgress';
import { Callout } from '../Callout';
import { Checkbox } from '../Checkbox';

import { TreeViewItem as Item } from './Item.interface';
import { useStyles } from './TreeItem.styles';

/**
 * Базовые свойства компонента {@link TreeItem}
 */
interface BaseTreeItemProps {
  /**
   * Объект данных, представляющий элемент дерева.
   */
  item: Item;

  /**
   * Нужно ли отображать линии для элемента дерева.
   *
   * @defaultValue `true`
   */
  showRulers?: boolean;

  /**
   * Нужно ли отображать счётчики, связанные с элементом дерева.
   *
   * @defaultValue `true`
   */
  showCounters?: boolean;

  /**
   * Массив идентификаторов узлов, которые в настоящее время развернуты.
   *
   * @defaultValue `[]`
   */
  expanded?: string[];

  /**
   * Колбэк-функция, вызываемая при клике на метку элемента дерева.
   * Принимает три аргумента: идентификатор узла, метку узла и сам элемент.
   */
  onSelect: (value: string, label?: string, item?: Item) => void;

  /**
   * Колбэк-функция, вызываемая при клике на иконку элемента дерева,
   * используется для разворачивания или сворачивания элемента. Принимает один аргумент: идентификатор узла.
   */
  onExpand: (value: string) => void;

  /**
   * Асинхронная функция для загрузки дочерних элементов элемента дерева.
   * Принимает один аргумент: идентификатор юнита и возвращает промис, который разрешается в массив элементов.
   */
  onChildrenLoad?: (unitId: string) => Promise<Item[]>;

  /**
   * Функция для кастомного рендеринга опции.
   */
  renderOption?: (item: Item) => ReactNode;
}

interface SingleSelectTreeItemProps extends BaseTreeItemProps {
  multiple?: false;
  selected: string;
}
interface MultiSelectTreeItemProps extends BaseTreeItemProps {
  multiple: true;
  selected: string[];
}

export type TreeItemProps =
  | SingleSelectTreeItemProps
  | MultiSelectTreeItemProps;

/**
 * Компонент элемента дерева, который может быть развернут и выбран, поддерживающий как одиночный, так и множественный выбор.
 * Он также может асинхронно загружать дочерние элементы и обрабатывать состояния загрузки и ошибок.
 *
 * @internal
 * @param item - Объект данных, представляющий элемент дерева.
 * @param showRulers - Нужно ли отображать линии для элемента дерева.
 * @param showCounters - Нужно ли отображать счётчики, связанные с элементом дерева.
 * @param expanded - Массив идентификаторов узлов, которые в настоящее время развернуты.
 * @param onSelect - Колбэк-функция, вызываемая при клике на метку элемента дерева.
 * @param onExpand - Колбэк-функция, вызываемая при клике на иконку элемента дерева.
 * @param onChildrenLoad - Асинхронная функция для загрузки дочерних элементов элемента дерева.
 * @param multiple - Указывает, поддерживает ли дерево множественный выбор.
 * @param selected - Идентификатор текущего выбранного узла для одиночного выбора или массив идентификаторов выбранных узлов для множественного выбора.
 * @param renderOption - Функция для кастомного рендеринга опции.
 * @returns Компонент TreeItem.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TreeItemProps}
 */
export const TreeItem = (props: TreeItemProps) => {
  const {
    item,
    showRulers = true,
    showCounters = true,
    expanded,
    onSelect,
    onExpand,
    onChildrenLoad,
    renderOption,
  } = props;

  const [loadedChildren, setLoadedChildren] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const isExpanded = expanded?.some(
    (expandedItems) => expandedItems === item.id,
  );

  const { classes: rawClasses } = useStyles();
  const { itemLabel, itemCounter, groupTransition, label, ...classes } =
    rawClasses;

  useEffect(() => {
    if (
      isExpanded &&
      item.hasChildrenToLoad &&
      !item.children?.length &&
      !loadedChildren.length
    ) {
      setIsLoading(true);
      setIsError(false);
      onChildrenLoad?.(item.id)
        .then((result) => {
          setIsLoading(false);
          setLoadedChildren(result);
        })
        .catch(() => {
          setIsLoading(false);
          setIsError(true);
          setLoadedChildren([]);
        });
    }
  }, [
    isExpanded,
    item.children?.length,
    item.hasChildrenToLoad,
    item.id,
    loadedChildren.length,
    onChildrenLoad,
    item,
  ]);

  /**
   * Обработчик события клика на метку элемента дерева.
   * Предотвращает распространение события и вызывает колбэк onSelect с идентификатором и меткой узла.
   *
   * @param event - Событие клика на метке элемента дерева.
   */
  const handleLabelClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (item.disabled) return;
    onSelect(item.id, item.label, item);
  };

  /**
   * Обработчик события клика на иконку элемента дерева.
   * Предотвращает распространение события и вызывает колбэк onExpand с идентификатором узла.
   *
   * @param event - Событие клика на иконку элемента дерева.
   */
  const handleIconClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    onExpand(item.id);
  };

  /**
   * Определяет, является ли состояние чекбокса индетерминированным.
   * Индетерминированное состояние возникает, если часть дочерних узлов выбрана, а другая нет.
   * Если все дочерние узлы выбраны, проверяет состояние их дочерних узлов рекурсивно.
   *
   * @param value - Массив идентификаторов выбранных узлов.
   * @param option - Элемент дерева, для которого проверяется состояние.
   * @returns  - Флаг, указывающий, является ли состояние чекбокса индетерминированным.
   */
  const getIsIndeterminate = (value: string[], option: Item): boolean => {
    if (value.includes(option.id)) {
      if (option.children) {
        if (!option.children.every((child) => value.includes(child.id))) {
          return true;
        } else {
          return option.children.every((child) =>
            getIsIndeterminate(value, child),
          );
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const childrenToShow =
    item?.children && !!item.children.length ? item.children : loadedChildren;
  const isShowChildren = !!childrenToShow.length || item.hasChildrenToLoad;

  const labelContent = renderOption ? (
    renderOption(item)
  ) : (
    <EllipsisText wordBreak="break-all">{item.label}</EllipsisText>
  );

  return (
    <MUITreeItem
      itemId={item.id}
      classes={{
        ...classes,
        ...(showRulers ? { groupTransition } : {}),
        label: classNames(label, 'aqa_tree_item'),
      }}
      slotProps={{
        label: { onClick: (event) => handleLabelClick(event) },
        iconContainer: { onClick: (event) => handleIconClick(event) },
      }}
      data-testid="tree-item-root-test-id"
      label={
        <div
          className={
            item.disabled ? rawClasses.itemDisabled : rawClasses.itemLabel
          }
        >
          {props.multiple ? (
            <Box display="flex" alignItems="center" gap="8">
              <Checkbox
                checked={props.selected.includes(item.id)}
                indeterminate={getIsIndeterminate(props.selected, item)}
                disabled={item.disabled}
              />
              {labelContent}
            </Box>
          ) : (
            labelContent
          )}
          <Box display="flex" gap="8">
            {item.isSeparate && !item.hasSeparateAbove && (
              <ColoredChip
                startIcon={
                  <Icon
                    name="vkhrtek_separate_unit"
                    color="text.light.tertirary"
                    size="12"
                  />
                }
                size="extraSmall"
                color="darkgray"
              />
            )}
            {showCounters && Number.isInteger(item.count) && (
              <div className={itemCounter}>{item.count}</div>
            )}
          </Box>
        </div>
      }
    >
      {isShowChildren && (
        <>
          {childrenToShow &&
            childrenToShow.map((child) => (
              <TreeItem
                key={child.id}
                item={child}
                showRulers={showRulers}
                showCounters={showCounters}
                onSelect={onSelect}
                onExpand={onExpand}
                onChildrenLoad={onChildrenLoad}
                expanded={expanded}
                renderOption={renderOption}
                {...(props.multiple
                  ? {
                      multiple: props.multiple,
                      selected: props.selected,
                    }
                  : { selected: props.selected })}
              />
            ))}
          {isLoading && (
            <Box
              display="flex"
              justifyContent="center"
              py="16"
              data-testid="circular-progress-loader-test-id"
            >
              <CircularProgress size={20} />
            </Box>
          )}
          {isError && (
            <Box py="24" px="16">
              <Callout variant="error" showIcon>
                Не удалось загрузить оргструктуру. Обновите страницу и
                попробуйте еще раз
              </Callout>
            </Box>
          )}
        </>
      )}
    </MUITreeItem>
  );
};
